import { useCallback, useMemo, useState, type ReactNode } from 'react'

import {
  I18nContext,
  applyLocale,
  resolveLocale,
  storeLocale,
  translate,
  type I18n,
  type Locale,
  type StringId,
} from './i18n'

/**
 * Holds the resolved locale and keeps `<html lang>` in step with it.
 *
 * The initial resolution runs in `useState`'s initialiser rather than in an
 * effect, so the first render is already in the right language — the same reason
 * `initTheme()` runs before the first paint in `main.tsx`. A locale applied in an
 * effect renders Bahasa and then flips, which is visible and looks like a bug.
 *
 * SUAP-0110.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const resolved = resolveLocale()
    applyLocale(resolved)
    return resolved
  })

  const setLocale = useCallback((next: Locale) => {
    storeLocale(next)
    applyLocale(next)
    setLocaleState(next)
  }, [])

  const value = useMemo<I18n>(
    () => ({
      locale,
      t: (key: StringId, vars?: Record<string, string | number>) => translate(locale, key, vars),
      setLocale,
    }),
    [locale, setLocale],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
